import { BlockOutlined, ToolOutlined, UnorderedListOutlined, WarningOutlined } from '@ant-design/icons'
import type { ViewFieldsFragment } from '@salescore/client-api'
import { SuspenseWithLoading, useMe } from '@salescore/client-common'
import {
  type CompiledFormViewConfig,
  type CompiledKpiPivotViewConfig,
  type CompiledKpiViewConfig,
  type CompiledSheetViewConfig,
  CORE_CONSTANT,
  type NodePropertyName,
  type ViewConfigTreeNode,
  type ViewQueryList,
} from '@salescore/core'
import { canUseOrganizationLicense, ORGANIZATION_LICENSES } from '@salescore/features'
import { Tabs, type TabsProps, Tooltip } from 'antd'
import { Array, Option } from 'effect'
import { t } from 'i18next'
import type { CSSProperties, ReactNode } from 'react'
import { useRecoilValue } from 'recoil'

import type { RSheetColumn, RSheetRecordNode } from '../../../../domain/column.ts'
import { sheetCustomModelsAtom } from '../../../../recoil/view/atoms'
import { KPI_CHILD_COUNT_WARNING_THRESHOLD } from '../../ViewUIKpiPivot/KpiForm/KpiFormChildNodeFormItem'
import type { ReferenceToPropertyForAddChildNodeMutation } from '../findSameReferenceToChild'
import { TABLE_HEIGHT } from './const'
import { PropertySelectorTableBody } from './PropertySelectorTableBody'
import { PropertySelectorTableBodyForParentChildrenNode } from './PropertySelectorTableBodyForParentChildrenNode'
import { SheetCustomModelPropertyForm } from './SheetCustomModelPropertyForm'

export const PropertySelectorTable = ({
  node,
  parentNode,
  addNodes,
  style,
  columns,
  compiledConfig,
  view,
  query,
}: {
  node: ViewConfigTreeNode
  parentNode: ViewConfigTreeNode | undefined
  addNodes: ({
    toggleNodeProperties,
    referenceToProperty,
    option,
  }: {
    toggleNodeProperties?: NodePropertyName[]
    referenceToProperty?: ReferenceToPropertyForAddChildNodeMutation
    option?: { skipSetCurrentNodePath?: boolean }
  }) => void
  style?: CSSProperties
  columns: Array<RSheetColumn<RSheetRecordNode>>
  compiledConfig: CompiledSheetViewConfig | CompiledKpiViewConfig | CompiledKpiPivotViewConfig | CompiledFormViewConfig
  view: ViewFieldsFragment
  query: ViewQueryList
}): ReactNode => {
  const me = useMe()
  const canUseCustomColumn = canUseOrganizationLicense(me.myUser, ORGANIZATION_LICENSES.customColumn)
  const sheetCustomModels = useRecoilValue(sheetCustomModelsAtom)
  const isSheetCustomModelNode = sheetCustomModels.some((x) => x.name === node.name) // これ以外の方法で判定できるようにしたいが、仕方ない？
  // eslint-disable-next-line sonarjs/todo-tag
  const isKpiSheet = view.id.startsWith(CORE_CONSTANT.KPI_SHEET_DYNAMIC_VIEW_ID_PREFIX) // TODO: これで判定するのやめたい

  const items: TabsProps['items'] = Array.getSomes([
    isSheetCustomModelNode
      ? Option.none()
      : Option.some({
          key: '1',
          label: (
            <>
              <UnorderedListOutlined />
              {t(`項目`)}
            </>
          ),
          children: <PropertySelectorTableBody node={node} addNodes={addNodes} columns={columns} />,
        }),
    isSheetCustomModelNode
      ? Option.none()
      : Option.some({
          key: '2',
          label: (
            <>
              <BlockOutlined />
              <Tooltip
                title={
                  shouldShowChildeNodeToolTip(view, isKpiSheet) ? (
                    <>
                      <WarningOutlined />
                      {t(`「関連オブジェクト」の数が多すぎると表示に時間がかかるため、設定数は5以下を推奨しています。`)}
                    </>
                  ) : undefined
                }
              >
                {t(`関連オブジェクト`)}
              </Tooltip>
            </>
          ),
          children: <PropertySelectorTableBodyForParentChildrenNode node={node} addChildNode={addNodes} />,
        }),
    // KPIのシートでカスタム列を扱うとややこしくなるので、一旦作らせない
    isKpiSheet || !canUseCustomColumn
      ? Option.none()
      : Option.some({
          key: '3',
          label: (
            <>
              <ToolOutlined />
              {t(`カスタム`)}
            </>
          ),
          children: (
            <SuspenseWithLoading>
              <SheetCustomModelPropertyForm
                node={node}
                parentNode={parentNode}
                addNodes={addNodes}
                compiledConfig={compiledConfig}
                view={view}
                query={query}
              />
            </SuspenseWithLoading>
          ),
        }),
  ])

  return (
    <div className={`${node.name} absolute w-full bg-white`} style={style ?? { height: TABLE_HEIGHT }}>
      <Tabs defaultActiveKey="1" type="card" items={items} />
    </div>
  )
}

// 関連オブジェクトの数は5以下に抑えて欲しいため、それを超えて追加を試みている場合は警告を表示する
function shouldShowChildeNodeToolTip(view: ViewFieldsFragment, isKpiSheet: boolean) {
  if (!isKpiSheet || view.config.type !== 'sheet') {
    return false
  }
  // ベースとなるオブジェクトも含まれるため-1する
  return (
    (view.config.fields === undefined ? 0 : Array.dedupe(view.config.fields.map((x) => x.property.nodeName)).length) -
      1 >=
    KPI_CHILD_COUNT_WARNING_THRESHOLD
  )
}
